<template>
  <div
    class="ui-multi-select"
    :class="[
      `ui-multi-select--${size}`,
      {
        'ui-multi-select--open': isOpen,
        'ui-multi-select--selected': selectedValues.length > 0,
        'ui-multi-select--error': error,
        'ui-multi-select--disabled': disabled,
        'ui-multi-select--view': viewOnly,
        'ui-multi-select--focused': isFocused,
      },
    ]"
    :style="{ width }"
    :data-id="uniqueId"
  >
    <!-- Selectize 스타일 입력 영역 -->
    <div class="ui-multi-select__control" @click="focusInput">
      <!-- 선택된 항목 태그들 -->
      <div class="ui-multi-select__tags">
        <template v-for="option in selectedOptions" :key="option.value">
          <ActivityUserHoverTooltip
            v-if="hasUserTooltip(option)"
            :user-id="getTagUserId(option)"
            :user-name="getTagUserName(option)"
          >
            <div
              class="ui-multi-select__tag"
              :class="{
                'ui-multi-select__tag--gray': (option.tagVariant || tagVariant) === 'gray',
                'ui-multi-select__tag--blue': (option.tagVariant || tagVariant) === 'blue',
                'ui-multi-select__tag--red': (option.tagVariant || tagVariant) === 'red',
              }"
            >
              <div class="ui-multi-select__tag-avatar">
                <img v-if="option.avatar" :src="option.avatar" :alt="option.label" class="ui-multi-select__avatar-image" />
              </div>
              <span class="ui-multi-select__tag-label">{{ option.label }}</span>
              <button v-if="!disabled && !viewOnly" class="ui-multi-select__tag-remove" @click.stop="removeOption(option)" type="button">×</button>
            </div>
          </ActivityUserHoverTooltip>
          <div
            v-else
            class="ui-multi-select__tag"
            :class="{
              'ui-multi-select__tag--gray': (option.tagVariant || tagVariant) === 'gray',
              'ui-multi-select__tag--blue': (option.tagVariant || tagVariant) === 'blue',
              'ui-multi-select__tag--red': (option.tagVariant || tagVariant) === 'red',
            }"
          >
            <div class="ui-multi-select__tag-avatar">
              <img v-if="option.avatar" :src="option.avatar" :alt="option.label" class="ui-multi-select__avatar-image" />
            </div>
            <span class="ui-multi-select__tag-label" :title="option.label">{{ option.label }}</span>
            <button v-if="!disabled && !viewOnly" class="ui-multi-select__tag-remove" @click.stop="removeOption(option)" type="button">×</button>
          </div>
        </template>

        <!-- 검색 입력 필드 -->
        <div class="ui-multi-select__input-wrapper">
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="ui-multi-select__input"
            :placeholder="selectedOptions.length === 0 ? displayPlaceholder : ''"
            :disabled="disabled || viewOnly"
            @focus="handleFocus"
            @keydown="handleKeydown"
            @input="handleInput"
            @keyup="handleInput"
            @compositionupdate="handleInput"
            autocomplete="off"
          />
        </div>
      </div>

      <!-- 로딩 스피너 또는 드롭다운 아이콘 -->
      <div class="ui-multi-select__indicators">
        <div v-if="loading" class="ui-multi-select__spinner"></div>
        <i v-else class="ui-multi-select__icon" :class="{ 'ui-multi-select__icon--up': isOpen }"></i>
      </div>
    </div>

    <!-- 드롭다운: 기본 teleport( body ) — 모달 overflow·뷰포트 하단에서 잘림 방지 -->
    <Teleport to="body" :disabled="!teleport">
      <div
        v-show="isOpen"
        ref="dropdownRef"
        class="ui-multi-select__dropdown"
        :class="{ 'ui-multi-select__dropdown--teleported': teleport }"
        :style="teleport ? dropdownStyle : {}"
      >
        <!-- 검색 결과가 없을 때 -->
        <div v-if="filteredOptions.length === 0 && searchQuery" class="ui-multi-select__no-results">
          <div class="ui-multi-select__no-results-icon">🔍</div>
          <div class="ui-multi-select__no-results-text">{{ noResultsText }}</div>
        </div>

        <!-- 옵션이 없을 때 -->
        <div v-else-if="options.length === 0 && !searchQuery" class="ui-multi-select__empty">
          <div class="ui-multi-select__empty-icon">👥</div>
          <div class="ui-multi-select__empty-text">{{ displayEmptyText }}</div>
        </div>

        <!-- 옵션 목록 -->
        <div v-else class="ui-multi-select__options">
          <!-- 전체 선택 행 (검색 결과 기준) -->
          <div
            v-if="showSelectAll && filteredOptions.length > 0"
            class="ui-multi-select__option ui-multi-select__option--select-all"
            :class="{ 'ui-multi-select__option--selected': isAllFilteredSelected }"
            @click.stop="toggleAll"
          >
            <div class="ui-multi-select__option-info">
              <div class="ui-multi-select__option-name">
                {{ selectAllLabel }}
                <span class="ui-multi-select__select-all-count">({{ filteredSelectedCount }} / {{ filteredSelectableCount }})</span>
              </div>
            </div>
            <div class="ui-multi-select__option-checkbox">
              <div
                class="ui-multi-select__checkbox"
                :class="{
                  'ui-multi-select__checkbox--checked': isAllFilteredSelected,
                  'ui-multi-select__checkbox--indeterminate': isPartialFilteredSelected,
                }"
              >
                <svg v-if="isAllFilteredSelected" viewBox="0 0 16 16" class="ui-multi-select__check-icon">
                  <path
                    d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                  />
                </svg>
                <span v-else-if="isPartialFilteredSelected" class="ui-multi-select__indeterminate-icon"></span>
              </div>
            </div>
          </div>
          <div
            v-for="(option, index) in filteredOptions"
            :key="option.value"
            class="ui-multi-select__option"
            :class="{
              'ui-multi-select__option--selected': isOptionChecked(option) || isOptionIndeterminate(option),
              'ui-multi-select__option--highlighted': highlightedIndex === index,
            }"
            @click.stop="toggleOption(option)"
            @mouseenter="highlightedIndex = index"
          >
            <!-- 옵션 정보 -->
            <div class="ui-multi-select__option-info">
              <div class="ui-multi-select__option-name" :title="option.label">{{ option.label }}</div>
              <div v-if="option.email" class="ui-multi-select__option-email">{{ option.email }}</div>
              <div v-if="option.department" class="ui-multi-select__option-department">
                {{ option.department }}
              </div>
            </div>

            <!-- 선택 체크박스 -->
            <div class="ui-multi-select__option-checkbox">
              <div
                class="ui-multi-select__checkbox"
                :class="{
                  'ui-multi-select__checkbox--checked': isOptionChecked(option),
                  'ui-multi-select__checkbox--indeterminate': isOptionIndeterminate(option),
                }"
              >
                <svg v-if="isOptionChecked(option)" viewBox="0 0 16 16" class="ui-multi-select__check-icon">
                  <path
                    d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                  />
                </svg>
                <span v-else-if="isOptionIndeterminate(option)" class="ui-multi-select__indeterminate-icon"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { UI_OVERLAY_DISMISS_EVENT } from '~/composables/useOverlayDismiss';
import ActivityUserHoverTooltip from '~/components/domain/Activity/ActivityUserHoverTooltip.vue';

const { t, locale } = useI18n();

const getTagUserId = option => {
  if (!option || option.isDept) return '';
  return String(option.userId ?? option.value ?? '');
};

const getTagUserName = option => String(option.userName ?? option.userNm ?? '').trim();

const hasUserTooltip = option => {
  if (!option || option.isDept) return false;
  return Boolean(option.userId || option.userName || option.userNm);
};

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    // 각 옵션은 { value: 'value', label: '이름', email: '이메일', department: '부서' } 형태
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  emptyText: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  width: {
    type: [String, Number],
    default: '100%',
  },
  tagVariant: {
    type: String,
    default: 'blue',
    validator: value => ['blue', 'gray', 'red'].includes(value),
  },
  // true: 드롭다운을 body로 렌더링(모달 overflow 회피). false: 인라인(레거시)
  teleport: {
    type: Boolean,
    default: true,
  },
  // 드롭다운 최대 높이(px). 뷰포트 여유에 맞춰 더 줄어듦
  maxHeight: {
    type: [String, Number],
    default: 300,
  },
  // 전체 선택 체크박스 표시 여부 (검색 결과 기준 일괄 선택/해제)
  showSelectAll: {
    type: Boolean,
    default: true,
  },
  // 선택한 옵션의 value와 같은 deptId를 가진 사용자 옵션들을 함께 선택
  autoSelectSameDept: {
    type: Boolean,
    default: false,
  },
  // modelValue가 외부에서 바뀔 때 검색어 초기화 (기본 false: 기존 동작 유지)
  clearSearchOnModelChange: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'search', 'focus', 'blur']);

const isOpen = ref(false);
const isFocused = ref(false);
const selectedValues = ref([...props.modelValue]);
const searchQuery = ref('');
const highlightedIndex = ref(-1);

const displayPlaceholder = computed(() => props.placeholder || t('common.multiSelect.placeholder'));
const displayEmptyText = computed(() => props.emptyText || t('common.multiSelect.emptyText'));
const selectAllLabel = computed(() => {
  locale.value;
  return t('common.multiSelect.selectAll');
});
const noResultsText = computed(() => {
  locale.value;
  return t('common.multiSelect.noResultsFor', { query: searchQuery.value });
});

const isKeyboardNavigating = ref(false); // 키보드 네비게이션 상태 추적
const searchInput = ref(null);
const dropdownRef = ref(null);
const uniqueId = ref(`multi-select-${Math.random().toString(36).substr(2, 9)}`);
const emptyDeptSelectedValues = ref([]);

// 드롭다운 위치 (Teleport + fixed, UiSelect와 동일하게 뷰포트 기준 플립)
const dropdownLayout = ref({
  direction: 'down',
  top: 0,
  bottom: 0,
  left: 0,
  width: 0,
  maxHeight: 300,
});

const parseMaxHeightProp = () => {
  const m = props.maxHeight;
  return typeof m === 'number' ? m : parseInt(String(m), 10) || 300;
};

/** UiFilterModal·UiModal 안에서 옵션 목록이 패널 뒤로 가려지지 않도록 부모 z-index + 10 (UiSelect와 동일) */
const resolveDropdownZIndex = () => {
  const root = document.querySelector(`.ui-multi-select[data-id="${uniqueId.value}"]`);
  if (!root) return 10060;

  const filterPanel = root.closest('.filter-dropdown');
  if (filterPanel) {
    const z = parseInt(window.getComputedStyle(filterPanel).zIndex, 10);
    return (Number.isFinite(z) ? z : 10051) + 10;
  }

  const popup = root.closest('.ui-popup');
  if (popup) {
    let z = parseInt(window.getComputedStyle(popup).zIndex, 10);
    if (!Number.isFinite(z)) {
      const content = popup.querySelector('.ui-popup__content');
      if (content) {
        z = parseInt(window.getComputedStyle(content).zIndex, 10);
      }
    }
    const base = Number.isFinite(z) ? z : 1000;
    return base + 10;
  }

  return 10060;
};

// 드롭다운 스타일 (Teleport용)
// 위로 열릴 때: bottom만 주면 높이가 확정되지 않아 안 보이는 경우가 있어 top+bottom 동시 지정
const dropdownStyle = computed(() => {
  if (!props.teleport || !isOpen.value) return {};
  const d = dropdownLayout.value;
  const style = {
    position: 'fixed',
    left: `${d.left}px`,
    width: `${d.width}px`,
    zIndex: resolveDropdownZIndex(),
    overflowY: 'auto',
  };
  if (d.direction === 'up') {
    style.top = `${d.top}px`;
    style.bottom = `${d.bottom}px`;
  } else {
    style.top = `${d.top}px`;
    style.maxHeight = `${d.maxHeight}px`;
  }
  return style;
});

// 선택된 옵션들 — selectedValues(선택 순서) 기준으로 태그 표시 (options 목록 순 X)
const selectedOptions = computed(() => {
  const optionByValue = new Map(props.options.map(option => [option.value, option]));
  return selectedValues.value
    .map(value => optionByValue.get(value))
    .filter(option => option && !(props.autoSelectSameDept && isDeptOption(option)));
});

// 필터링된 옵션들을 계산
const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;

  const query = searchQuery.value.toLowerCase();
  return props.options.filter(option => {
    return (
      option.label.toLowerCase().includes(query) ||
      (option.email && option.email.toLowerCase().includes(query)) ||
      (option.department && option.department.toLowerCase().includes(query))
    );
  });
});

// ===== 전체 선택 (검색 결과 기준) =====
const getFilteredSelectableOptions = () => {
  return filteredOptions.value.filter(opt => !(props.autoSelectSameDept && isDeptOption(opt)));
};

const getFilteredEmptyDeptOptions = () => {
  if (!props.autoSelectSameDept) return [];

  return filteredOptions.value.filter(option => {
    if (!isDeptOption(option)) return false;
    const sameDeptOptions = props.options.filter(item => !isDeptOption(item) && item.deptId === option.value && item.value !== option.value);
    return sameDeptOptions.length === 0;
  });
};

// 현재 필터링된 옵션 중 선택된 개수
const filteredSelectedCount = computed(() => {
  return getFilteredSelectableOptions().filter(opt => selectedValues.value.includes(opt.value)).length;
});

const filteredSelectableCount = computed(() => {
  return getFilteredSelectableOptions().length;
});

// 필터링된 옵션이 모두 선택됨
const isAllFilteredSelected = computed(() => {
  return filteredSelectableCount.value > 0 && filteredSelectedCount.value === filteredSelectableCount.value;
});

// 필터링된 옵션 중 일부만 선택됨 (indeterminate 상태)
const isPartialFilteredSelected = computed(() => {
  return filteredSelectedCount.value > 0 && filteredSelectedCount.value < filteredSelectableCount.value;
});

// 전체 선택/해제 토글 — 검색 결과 기준
const toggleAll = () => {
  if (props.disabled || props.viewOnly) return;

  const filteredValues = getFilteredSelectableOptions().map(opt => opt.value);
  const filteredEmptyDeptValues = getFilteredEmptyDeptOptions().map(opt => opt.value);

  if (isAllFilteredSelected.value) {
    // 전체 선택 상태 → 검색 결과만 해제 (다른 검색어로 선택된 항목은 유지)
    selectedValues.value = selectedValues.value.filter(v => !filteredValues.includes(v));
    emptyDeptSelectedValues.value = emptyDeptSelectedValues.value.filter(v => !filteredEmptyDeptValues.includes(v));
  } else {
    // 일부/미선택 상태 → 검색 결과 모두 추가 (중복 제거)
    selectedValues.value = Array.from(new Set([...selectedValues.value, ...filteredValues]));
    emptyDeptSelectedValues.value = Array.from(new Set([...emptyDeptSelectedValues.value, ...filteredEmptyDeptValues]));
  }

  emit('update:modelValue', selectedValues.value);

  // 검색어는 유지 (일괄 선택 후 다음 키워드로 전환하는 흐름이 자연스러움)
  // 페이지 스크롤 방지 + 컨트롤 크기 변화에 맞춰 드롭다운 위치 재계산
  nextTick(() => {
    searchInput.value?.focus({ preventScroll: true });
    if (props.teleport && isOpen.value) updateDropdownPosition();
  });
};

// 드롭다운 위치 업데이트 (모달 밖 body 렌더 + 화면 하단/상단 여유에 따라 위로 열기)
const updateDropdownPosition = () => {
  if (!props.teleport) return;
  const control = document.querySelector(`.ui-multi-select[data-id="${uniqueId.value}"] .ui-multi-select__control`);
  if (!control) return;

  const rect = control.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  const gap = 4;
  const edgeMargin = 8;
  const preferredMax = parseMaxHeightProp();

  const optCount = filteredOptions.value.length + (props.showSelectAll && filteredOptions.value.length > 0 ? 1 : 0);
  const estimatedDropdownHeight = Math.min(Math.max(optCount, 1) * 48 + 24, preferredMax);

  const spaceBelow = windowHeight - rect.bottom - gap;
  const spaceAbove = rect.top - edgeMargin;

  let direction = 'down';
  if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
    direction = 'up';
  }

  let left = rect.left;
  let width = rect.width;
  if (left + width > windowWidth - edgeMargin) {
    left = Math.max(edgeMargin, windowWidth - width - edgeMargin);
  }
  if (left < edgeMargin) {
    left = edgeMargin;
  }

  const maxHeightDown = Math.min(preferredMax, Math.max(120, spaceBelow));
  const maxHeightUp = Math.min(preferredMax, Math.max(120, spaceAbove));

  if (direction === 'up') {
    // 하단: 컨트롤 상단 직전(rect.top - gap) / 상단: 최대 maxHeightUp만큼 위로(상단 여백 edgeMargin)
    const bottomInset = windowHeight - rect.top + gap;
    const topInset = Math.max(edgeMargin, rect.top - gap - maxHeightUp);
    dropdownLayout.value = {
      direction: 'up',
      top: topInset,
      bottom: bottomInset,
      left,
      width,
      maxHeight: maxHeightUp,
    };
  } else {
    dropdownLayout.value = {
      direction: 'down',
      top: rect.bottom + gap,
      bottom: 0,
      left,
      width,
      maxHeight: maxHeightDown,
    };
  }
};

// 이니셜 생성 함수
const getInitials = name => {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .substring(0, 2);
};

// 옵션이 선택되었는지 확인
const isSelected = option => {
  return selectedValues.value.includes(option.value);
};

const isDeptOption = option => {
  if (!option) return false;
  if (option.isDept || option.type === 'dept' || option.optionType === 'dept') return true;
  if (props.autoSelectSameDept && (option.deptId == null || option.deptId === '')) return true;
  return Boolean(option.deptId && option.value === option.deptId);
};

const getSameDeptOptions = option => {
  if (!props.autoSelectSameDept || !isDeptOption(option)) return [];
  return props.options.filter(item => !isDeptOption(item) && item.deptId === option.value && item.value !== option.value);
};

const getDeptSelectionState = option => {
  const sameDeptValues = getSameDeptOptions(option).map(item => item.value);
  if (sameDeptValues.length === 0) {
    const emptyChecked = emptyDeptSelectedValues.value.includes(option.value);
    return { checked: emptyChecked, indeterminate: false, sameDeptValues };
  }

  const selectedCount = sameDeptValues.filter(value => selectedValues.value.includes(value)).length;
  return {
    checked: selectedCount === sameDeptValues.length,
    indeterminate: selectedCount > 0 && selectedCount < sameDeptValues.length,
    sameDeptValues,
  };
};

const isOptionChecked = option => {
  return isDeptOption(option) ? getDeptSelectionState(option).checked : isSelected(option);
};

const isOptionIndeterminate = option => {
  return isDeptOption(option) ? getDeptSelectionState(option).indeterminate : false;
};

const toggleEmptyDeptSelection = option => {
  const index = emptyDeptSelectedValues.value.indexOf(option.value);
  if (index === -1) {
    emptyDeptSelectedValues.value.push(option.value);
  } else {
    emptyDeptSelectedValues.value.splice(index, 1);
  }
};

const toggleDeptOption = option => {
  const { checked, sameDeptValues } = getDeptSelectionState(option);

  if (sameDeptValues.length === 0) {
    toggleEmptyDeptSelection(option);
    return;
  }

  emptyDeptSelectedValues.value = emptyDeptSelectedValues.value.filter(value => value !== option.value);

  if (checked) {
    selectedValues.value = selectedValues.value.filter(value => !sameDeptValues.includes(value));
  } else {
    selectedValues.value = Array.from(new Set([...selectedValues.value, ...sameDeptValues]));
  }
};

// 옵션 토글 (선택/해제)
const toggleOption = option => {
  if (props.disabled || props.viewOnly) return;

  if (props.autoSelectSameDept && isDeptOption(option)) {
    toggleDeptOption(option);
    emit('update:modelValue', selectedValues.value);
    searchQuery.value = '';
    nextTick(() => {
      searchInput.value?.focus({ preventScroll: true });
      if (props.teleport && isOpen.value) updateDropdownPosition();
    });
    return;
  }

  const index = selectedValues.value.indexOf(option.value);
  if (index === -1) {
    selectedValues.value.push(option.value);
  } else {
    selectedValues.value.splice(index, 1);
  }
  emit('update:modelValue', selectedValues.value);

  // 검색어 초기화 및 포커스 유지 (페이지 스크롤 방지 + 드롭다운 위치 재계산)
  searchQuery.value = '';
  nextTick(() => {
    searchInput.value?.focus({ preventScroll: true });
    if (props.teleport && isOpen.value) updateDropdownPosition();
  });
};

// 옵션 제거
const removeOption = option => {
  if (props.disabled || props.viewOnly) return;

  const index = selectedValues.value.indexOf(option.value);
  if (index !== -1) {
    selectedValues.value.splice(index, 1);
    emit('update:modelValue', selectedValues.value);
  }
};

// 입력 필드에 포커스
const focusInput = () => {
  if (props.disabled || props.viewOnly) return;
  searchInput.value?.focus();
};

// 포커스 핸들러
const handleFocus = () => {
  isFocused.value = true;
  isOpen.value = true;
  isKeyboardNavigating.value = false; // 네비게이션 상태 초기화
  // 드롭다운이 열릴 때 첫 번째 옵션을 하이라이트
  highlightedIndex.value = filteredOptions.value.length > 0 ? 0 : -1;
  emit('focus');

  // teleport 모드일 때만 드롭다운 위치 업데이트
  if (props.teleport) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }

  // 다른 셀렉트 드롭다운 닫기
  window.dispatchEvent(
    new CustomEvent('select-dropdown-toggle', {
      detail: { id: uniqueId.value },
    }),
  );
};

// 입력 핸들러
const handleInput = e => {
  searchQuery.value = e.target.value;
  emit('search', searchQuery.value);

  // 키보드 네비게이션 중이 아닐 때만 하이라이트 인덱스 리셋
  if (!isKeyboardNavigating.value) {
    highlightedIndex.value = filteredOptions.value.length > 0 ? 0 : -1;
  }

  if (props.teleport && isOpen.value) {
    nextTick(() => updateDropdownPosition());
  }
};

// 하이라이트된 옵션을 뷰포트에 보이도록 스크롤
const scrollToHighlightedOption = () => {
  nextTick(() => {
    const dropdown = dropdownRef.value;
    const highlightedOption = dropdown?.querySelector('.ui-multi-select__option--highlighted');

    if (highlightedOption && dropdown) {
      const dropdownRect = dropdown.getBoundingClientRect();
      const optionRect = highlightedOption.getBoundingClientRect();

      // 옵션이 드롭다운 상단 밖에 있으면 스크롤 업
      if (optionRect.top < dropdownRect.top) {
        highlightedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      // 옵션이 드롭다운 하단 밖에 있으면 스크롤 다운
      else if (optionRect.bottom > dropdownRect.bottom) {
        highlightedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  });
};

// 키보드 네비게이션
const handleKeydown = event => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      isKeyboardNavigating.value = true; // 키보드 네비게이션 상태 설정
      if (highlightedIndex.value < filteredOptions.value.length - 1) {
        highlightedIndex.value++;
        scrollToHighlightedOption();
      }
      break;
    case 'ArrowUp':
      event.preventDefault();
      isKeyboardNavigating.value = true; // 키보드 네비게이션 상태 설정
      if (highlightedIndex.value > 0) {
        highlightedIndex.value--;
        scrollToHighlightedOption();
      }
      break;
    case 'Enter':
      event.preventDefault();
      isKeyboardNavigating.value = false; // 네비게이션 상태 해제
      if (highlightedIndex.value >= 0 && filteredOptions.value[highlightedIndex.value]) {
        toggleOption(filteredOptions.value[highlightedIndex.value]);
      }
      break;
    case 'Escape':
      isKeyboardNavigating.value = false; // 네비게이션 상태 해제
      isOpen.value = false;
      searchInput.value?.blur();
      break;
    case 'Backspace':
      isKeyboardNavigating.value = false; // 네비게이션 상태 해제
      if (!searchQuery.value && selectedOptions.value.length > 0) {
        removeOption(selectedOptions.value[selectedOptions.value.length - 1]);
      }
      break;
    default:
      // 다른 키를 누르면 네비게이션 상태 해제
      isKeyboardNavigating.value = false;
      break;
  }
};

// 다른 셀렉트가 열릴 때 현재 셀렉트를 닫는 이벤트 핸들러
const handleOtherSelectOpen = event => {
  if (event.detail.id !== uniqueId.value && isOpen.value) {
    isOpen.value = false;
    isFocused.value = false;
  }
};

const handleOverlayDismiss = () => {
  if (isOpen.value) {
    isOpen.value = false;
    isFocused.value = false;
  }
};

// 문서 클릭(캡처): 부모의 @click.stop 으로 버블이 끊겨도 바깥 클릭 시 드롭다운 닫힘
const closeDropdown = event => {
  const multiSelect = event.target.closest('.ui-multi-select');
  const dropdown = event.target.closest('.ui-multi-select__dropdown--teleported');
  // multiSelect 컴포넌트 또는 teleport된 드롭다운 클릭 시에는 닫지 않음
  if (!multiSelect && !dropdown && isOpen.value) {
    isOpen.value = false;
    isFocused.value = false;
  }
};

// 스크롤 시 드롭다운 위치 업데이트
const handleScroll = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

// 모델 값 변경 시 내부 상태 업데이트
watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    selectedValues.value = [...newValue];
    // 내용이 실제로 바뀔 때만 검색어 초기화 (참조만 바뀌는 경우 유지)
    if (props.clearSearchOnModelChange) {
      const next = (newValue || []).map(v => String(v)).join('\0');
      const prev = (oldValue || []).map(v => String(v)).join('\0');
      if (next !== prev) {
        searchQuery.value = '';
      }
    }
  },
  { deep: true },
);

watch(isOpen, open => {
  if (open && props.teleport) {
    nextTick(() => updateDropdownPosition());
  }
});

watch(
  () => [filteredOptions.value.length, props.options.length],
  () => {
    if (props.teleport && isOpen.value) {
      nextTick(() => updateDropdownPosition());
    }
  },
);

// 컴포넌트 마운트 시 이벤트 리스너 추가
onMounted(() => {
  document.addEventListener('click', closeDropdown, true);
  window.addEventListener('select-dropdown-toggle', handleOtherSelectOpen);
  window.addEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  // teleport 모드일 때만 스크롤/리사이즈 이벤트 등록
  if (props.teleport) {
    window.addEventListener('scroll', handleScroll, true); // capture phase로 모든 스크롤 감지
    window.addEventListener('resize', handleScroll);
  }
});

// 컴포넌트 언마운트 시 이벤트 리스너 제거
onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown, true);
  window.removeEventListener('select-dropdown-toggle', handleOtherSelectOpen);
  window.removeEventListener(UI_OVERLAY_DISMISS_EVENT, handleOverlayDismiss);
  if (props.teleport) {
    window.removeEventListener('scroll', handleScroll, true);
    window.removeEventListener('resize', handleScroll);
  }
});
</script>

<style lang="scss" scoped>
// 애니메이션 키프레임
@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.ui-multi-select {
  position: relative;
  width: v-bind('width');
  font-family: $font-family;

  // 컨트롤 영역 (Selectize 스타일)
  &__control {
    display: flex;
    align-items: center;
    // height: $ui-height-md; // 36px
    border: 1px solid $border-color-light;
    border-radius: $border-radius-sm;
    background: var(--color-gray-0, #fff);
    cursor: text;
    transition: all $transition-normal ease;
    padding-left: 7px;
    box-sizing: border-box;

    &:hover {
      border-color: $border-color;
    }
  }

  // 태그 영역
  &__tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    flex: 1;
    gap: 4px;
    padding: 2px 0;
    min-height: 30px;
    max-height: 100px;
    overflow-y: auto;

    :deep(.activity-user-hover-tooltip__trigger) {
      display: inline-flex;
      max-width: 200px;
    }
  }

  // 선택된 항목 태그 스타일
  &__tag {
    display: inline-flex;
    align-items: center;
    background: var(--color-system-b10, #e6f3ff);
    border: 1px solid var(--color-system-b20, #b3d9ff);
    border-radius: 16px;
    padding: 4px 8px;
    font-size: $font-size-xs;
    color: var(--color-system-b40, #0066cc);
    gap: 4px;
    max-width: 200px;

    &:hover {
      background: var(--color-system-b15, #d9ecff);
    }

    // 그레이 색상 변형
    &--gray {
      background: var(--color-gray-10, #e6e8ea);
      border: 1px solid var(--color-gray-20, #cdd1d5);
      color: var(--color-gray-60, #58616a);

      &:hover {
        background: var(--color-gray-15, #dde0e3);
      }

      .ui-multi-select__tag-remove {
        color: var(--color-gray-50, #6c757d);

        &:hover {
          background: var(--color-gray-20, #cdd1d5);
          color: var(--color-gray-70, #464c53);
        }
      }
    }

    // 빨강 색상 변형 (인사목록에 없는 fallback 선택 등)
    &--red {
      background: var(--color-system-r10, #ffe6e6);
      border: 1px solid var(--color-system-r20, #ffb3b3);
      color: var(--color-system-r30, #f30);

      &:hover {
        background: var(--color-system-r15, #ffd6d6);
      }

      .ui-multi-select__tag-remove {
        color: var(--color-system-r30, #f30);

        &:hover {
          background: var(--color-system-r20, #ffb3b3);
          color: var(--color-system-r40, #cc0000);
        }
      }
    }
  }

  // 태그 라벨
  &__tag-label {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    font-size: 13px;
  }

  // 태그 제거 버튼
  &__tag-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: none;
    background: transparent;
    color: var(--color-system-b30, #0084ff);
    cursor: pointer;
    border-radius: 50%;
    font-size: 14px;
    line-height: 1;
    transition: all 0.2s ease;

    &:hover {
      background: var(--color-system-b20, #b3d9ff);
      color: var(--color-system-b50, #004499);
    }
  }

  // 검색 입력 필드
  &__input-wrapper {
    flex: 1;
    min-width: 100px;
  }

  &__input {
    border: none;
    outline: none;
    background: transparent;
    font-size: $ui-font-md;
    color: var(--color-gray-70, #464c53);
    width: 100%;
    min-width: 50px;

    &::placeholder {
      color: $placeholder-color;
      font-family: $placeholder-font-family;
      font-size: $ui-font-md;
      font-style: normal;
      font-weight: $placeholder-font-weight;
      line-height: $placeholder-line-height;
    }
  }

  // 인디케이터 영역
  &__indicators {
    display: flex;
    align-items: center;
    padding: 0 8px;
  }

  // 로딩 스피너
  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid var(--color-gray-20, #cdd1d5);
    border-top: 2px solid var(--color-system-b30, #0084ff);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  // 드롭다운 아이콘
  &__icon {
    width: 12px;
    height: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M11 1.5L6 6.5L1 1.5' stroke='%23464C53' stroke-width='2'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    transition: transform 0.2s ease;

    &--up {
      transform: rotate(180deg);
    }
  }

  // 드롭다운 메뉴
  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid var(--color-gray-20, #cdd1d5);
    border-radius: $border-radius-sm;
    margin-top: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    animation: dropdownFadeIn 0.2s ease;
    max-height: 300px;
    overflow-y: auto;
  }

  // 옵션 목록
  &__options {
    padding: 4px 0;
  }

  // 개별 옵션
  &__option {
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    gap: 12px;
    min-width: 0;

    &:hover,
    &--highlighted {
      background-color: var(--color-system-b5, #f0f8ff);
      border-left: 3px solid var(--color-system-b30, #0084ff);

      .ui-multi-select__option-name {
        color: var(--color-system-b40, #0066cc);
        font-weight: 600;
      }
    }

    &--selected {
      background-color: var(--color-system-b5, #f0f8ff);

      .ui-multi-select__option-name {
        color: var(--color-system-b40, #0066cc);
        font-weight: 600;
      }
    }

    // 전체 선택 행
    &--select-all {
      border-bottom: 1px solid var(--color-gray-15, #dde0e3);
      background-color: var(--color-gray-5, #f7f8f9);
      position: sticky;
      top: 0;
      z-index: 1;

      .ui-multi-select__option-name {
        font-weight: 600;
        color: var(--color-gray-70, #464c53);
      }

      &:hover {
        background-color: var(--color-system-b5, #f0f8ff);
        border-left: 3px solid var(--color-system-b30, #0084ff);
      }
    }
  }

  // 전체 선택 카운트
  &__select-all-count {
    margin-left: 6px;
    font-size: $font-size-xs;
    font-weight: 400;
    color: var(--color-gray-40, #8a949e);
  }

  // 옵션 정보
  &__option-info {
    flex: 1;
    min-width: 0;
  }

  &__option-name {
    font-size: $font-size-md;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    margin-bottom: 2px;
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  &__option-email {
    font-size: $font-size-xs;
    color: var(--color-gray-40, #8a949e);
    margin-bottom: 2px;
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  &__option-department {
    font-size: $font-size-xs;
    color: var(--color-gray-30, #b1b8be);
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  // 체크박스
  &__option-checkbox {
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-gray-20, #cdd1d5);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &--checked {
      background: var(--color-system-b30, #0084ff);
      border-color: var(--color-system-b30, #0084ff);
    }

    &--indeterminate {
      background: var(--color-system-b30, #0084ff);
      border-color: var(--color-system-b30, #0084ff);
    }
  }

  &__check-icon {
    width: 12px;
    height: 12px;
    fill: white;
  }

  // indeterminate 표시 (가운데 가로줄)
  &__indeterminate-icon {
    width: 10px;
    height: 2px;
    background: #fff;
    border-radius: 1px;
  }

  // 빈 상태 메시지
  &__empty,
  &__no-results {
    padding: 24px;
    text-align: center;
    color: var(--color-gray-40, #8a949e);
  }

  &__empty-icon,
  &__no-results-icon {
    font-size: 24px;
    margin-bottom: 8px;
  }

  &__empty-text,
  &__no-results-text {
    font-size: $font-size-md;
    font-weight: 500;
  }

  // 상태별 스타일
  &--focused {
    .ui-multi-select__control {
      border-color: var(--color-system-b30, #0084ff);
      box-shadow: 0 0 0 3px rgba(0, 132, 255, 0.1);
    }
  }

  &--error {
    .ui-multi-select__control {
      border-color: var(--color-system-r30, #f30);
      box-shadow: 0 0 0 3px rgba(255, 51, 0, 0.1);
    }
  }

  &--disabled {
    .ui-multi-select__control {
      background: var(--color-gray-10, #e6e8ea);
      border-color: var(--color-gray-20, #cdd1d5);
      cursor: not-allowed;
    }

    .ui-multi-select__input {
      color: var(--color-gray-30, #b1b8be);
    }
  }

  // viewOnly 스타일을 UiInput readonly와 동일하게 수정
  &--view {
    .ui-multi-select__control {
      background: var(--color-gray-10, #e6e8ea); // 읽기 전용 배경
      border: 1px solid $border-color-light;
      color: var(--color-gray-60, #58616a);
      cursor: default;
    }

    .ui-multi-select__tag-remove {
      display: none;
    }
  }

  // 크기 변형
  &--small {
    .ui-multi-select__control {
      //height: $ui-height-sm;
      box-sizing: border-box;
    }

    .ui-multi-select__input {
      font-size: $ui-font-sm;

      &::placeholder {
        font-size: $ui-font-sm;
      }
    }

    .ui-multi-select__tag {
      padding: 2px 6px;
      font-size: 13px;
    }

    .ui-multi-select__option {
      padding: 8px 12px;
    }
  }

  &--medium {
    .ui-multi-select__control {
      // height: $ui-height-md; // 36px
      box-sizing: border-box;
      padding: 3px 6px;
    }

    .ui-multi-select__input {
      font-size: $ui-font-md;

      &::placeholder {
        font-size: $ui-font-md;
      }
    }

    .ui-multi-select__tag {
      padding: 2px 8px;
      font-size: $font-size-xs;
    }

    .ui-multi-select__option {
      padding: 10px 12px;
    }
  }

  &--large {
    .ui-multi-select__control {
      height: $ui-height-lg; // 48px
      box-sizing: border-box;
    }

    .ui-multi-select__input {
      font-size: $ui-font-lg;

      &::placeholder {
        font-size: $ui-font-lg;
      }
    }

    .ui-multi-select__tag {
      padding: 6px 10px;
      font-size: $font-size-sm;
    }

    .ui-multi-select__option {
      padding: 12px 16px;
    }
  }

  &--xlarge {
    .ui-multi-select__control {
      height: $ui-height-xl; // 56px
      box-sizing: border-box;
    }

    .ui-multi-select__input {
      font-size: $ui-font-xl;

      &::placeholder {
        font-size: $ui-font-xl;
      }
    }

    .ui-multi-select__tag {
      padding: 8px 12px;
      font-size: $font-size-md;
    }

    .ui-multi-select__option {
      padding: 14px 20px;
    }
  }

  // ui-multi-select-gray-ver
  &.ui-multi-select-gray-ver {
    .ui-multi-select__control {
      padding: 7px 8px;
    }
    .ui-multi-select__tags {
      padding: 0;
      min-height: 24px;
    }
    .ui-multi-select__tag {
      border-radius: var(--radius-max, 1000px);
      border: 1px solid var(--color-border-gray-light, #cdd1d5);
      background: var(--color-action-white, #fff);
      color: #1e2124;

      &:hover {
        background: #eef2f7;
      }
    }

    .ui-multi-select__tag-label {
      line-height: 18px;
    }

    .ui-multi-select__tag-remove {
      position: relative;
      width: 14px;
      height: 14px;
      border-radius: 16px;
      background: var(--color-icon-gray-fill, #cdd1d5);
      // color: #CDD1D5;
      color: #1e2124;

      &:hover {
        background: #c2c5c9;
      }

      // &::before{
      //   content: '';
      //   position: absolute;
      //   top: 50%;
      //   left: 50%;
      //   transform: translate(-50%, -50%);
      //   width: 9px;
      //   height: 9px;
      //   margin: 0.5px 0 0 0.5px;

      //   background-image: url("data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.15709 0.15614C6.36531 -0.0520822 6.70271 -0.0519679 6.911 0.15614C7.11928 0.36442 7.11928 0.701767 6.911 0.910047L4.28698 3.53309L6.91002 6.15614C7.1183 6.36442 7.1183 6.70177 6.91002 6.91005C6.70174 7.11828 6.36438 7.11831 6.15612 6.91005L3.53307 4.287L0.910999 6.91005C0.702713 7.11825 0.365344 7.1183 0.157092 6.91005C-0.0509893 6.70178 -0.0510494 6.36437 0.157092 6.15614L2.77916 3.53309L0.156116 0.910047C-0.0520086 0.701772 -0.0520686 0.364378 0.156116 0.15614C0.364349 -0.052093 0.701731 -0.0520005 0.910022 0.15614L3.53307 2.77919L6.15709 0.15614Z' fill='%2333363D'/%3E%3C/svg%3E%0A");
      //   background-repeat: no-repeat;
      //   background-position: center center;
      //   background-size: contain;
      // }
    }
  }

  // 모바일 최적화
  @media (max-width: 768px) {
    &__dropdown {
      max-height: 250px;
    }

    &__option {
      padding: 12px;
      min-height: 56px;
    }

    &__tag {
      max-width: 150px;
    }
  }
}
</style>

<!-- Teleport된 드롭다운용 글로벌 스타일 -->
<style lang="scss">
// Teleport된 드롭다운 스타일 (scoped 적용 안 됨)
.ui-multi-select__dropdown--teleported {
  background: #fff;
  border: 1px solid var(--color-gray-20, #cdd1d5);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;

  // 옵션 목록
  .ui-multi-select__options {
    padding: 4px 0;
  }

  // 개별 옵션
  .ui-multi-select__option {
    display: flex;
    align-items: flex-start;
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    gap: 12px;
    min-width: 0;

    &:hover,
    &--highlighted {
      background-color: var(--color-system-b5, #f0f8ff);
      border-left: 3px solid var(--color-system-b30, #0084ff);

      .ui-multi-select__option-name {
        color: var(--color-system-b40, #0066cc);
        font-weight: 600;
      }
    }

    &--selected {
      background-color: var(--color-system-b5, #f0f8ff);

      .ui-multi-select__option-name {
        color: var(--color-system-b40, #0066cc);
        font-weight: 600;
      }
    }

    // 전체 선택 행
    &--select-all {
      border-bottom: 1px solid var(--color-gray-15, #dde0e3);
      background-color: var(--color-gray-5, #f7f8f9);
      position: sticky;
      top: 0;
      z-index: 1;

      .ui-multi-select__option-name {
        font-weight: 600;
        color: var(--color-gray-70, #464c53);
      }

      &:hover {
        background-color: var(--color-system-b5, #f0f8ff);
        border-left: 3px solid var(--color-system-b30, #0084ff);
      }
    }
  }

  // 전체 선택 카운트
  .ui-multi-select__select-all-count {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 400;
    color: var(--color-gray-40, #8a949e);
  }

  // 옵션 정보
  .ui-multi-select__option-info {
    flex: 1;
    min-width: 0;
  }

  .ui-multi-select__option-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-gray-70, #464c53);
    margin-bottom: 2px;
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  .ui-multi-select__option-email {
    font-size: 12px;
    color: var(--color-gray-40, #8a949e);
    margin-bottom: 2px;
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  .ui-multi-select__option-department {
    font-size: 12px;
    color: var(--color-gray-30, #b1b8be);
    line-height: 1.4;
    word-break: break-all;
    white-space: normal;
  }

  // 체크박스
  .ui-multi-select__option-checkbox {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .ui-multi-select__checkbox {
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-gray-20, #cdd1d5);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &--checked {
      background: var(--color-system-b30, #0084ff);
      border-color: var(--color-system-b30, #0084ff);
    }

    &--indeterminate {
      background: var(--color-system-b30, #0084ff);
      border-color: var(--color-system-b30, #0084ff);
    }
  }

  .ui-multi-select__check-icon {
    width: 12px;
    height: 12px;
    fill: white;
  }

  .ui-multi-select__indeterminate-icon {
    width: 10px;
    height: 2px;
    background: #fff;
    border-radius: 1px;
  }

  // 빈 상태 메시지
  .ui-multi-select__empty,
  .ui-multi-select__no-results {
    padding: 24px;
    text-align: center;
    color: var(--color-gray-40, #8a949e);
  }

  .ui-multi-select__empty-icon,
  .ui-multi-select__no-results-icon {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .ui-multi-select__empty-text,
  .ui-multi-select__no-results-text {
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
